import React, { useContext, useEffect } from 'react';
import classNames from 'classnames';
import { useDecision } from '@optimizely/react-sdk';
import { useUserContext } from '@fff-web/fff-utilities';
import { Cookies } from '@fff-web/fff-utilities';
import { useTrackOptimizely } from '@fff-web/fff-analytics';
import spacetime from 'spacetime';
import GlobalNavInternalContext from '../../context/GlobalNavInternalContext';
import { useNmfRedirect } from '../../hooks/useNmfRedirect';
import { useNmfTimer } from '../../hooks/useNmfTimer';
import NmfTimeUp from '../Modals/NmfTimeUp';
import NewMemberNav from '../NMF/NewMemberNav';
import TimerBar from '../TimerBar/TimerBar';
import './Header.css';
import NMFHeaderContent from './NMFHeaderContent';
import { buildUserInfoRequest } from '../../helpers/userAdapter';
import NavToast from '../Toast';

const TIMER_SOURCE = {
  introProduct: 'introProduct',
  publicCustomize: 'publicCustomize',
};

const NEW_MEMBER_XSELL_FLAG_COOKIE = 'fff_universal_nav_newMemberXSellsFlag';

const NMFHeader = ({ step, isEditCampaignOpen }) => {
  const userRequest = buildUserInfoRequest(useUserContext().userInfo);
  const { endTime, lastVisitedCustomizeQuestionId, timerType, timerSource } = useNmfTimer();
  const {
    env: globalEnv,
    newMemberState,
    onNewMemberStateFetched,
    flags,
  } = useContext(GlobalNavInternalContext);
  const {
    newMemberOnboardingFF: newMemberOnboardingFlag,
    newMemberOnboardingReady: newMemberOnboardingFlagReady,
  } = flags;
  const baseUrl = globalEnv.REACT_APP_BASE_URL;

  useNmfRedirect(
    newMemberState,
    lastVisitedCustomizeQuestionId,
    baseUrl,
    timerType,
  );

  const [newMemberXSellsDecision] = useDecision('new-member-xsells', {
    autoUpdate: true,
  });
  const { variationKey: newMemberXSellsFlag, enabled: newMemberXSellsFlagEnabled } =
    newMemberXSellsDecision;
  useTrackOptimizely(newMemberXSellsDecision, newMemberXSellsDecision.ruleKey);

  const showTimer = newMemberOnboardingFlagReady && !newMemberOnboardingFlag;

  const skipCustomize =
    timerSource === TIMER_SOURCE.introProduct || timerSource === TIMER_SOURCE.publicCustomize;

  const hideBoostNode =
    !newMemberOnboardingFlag ||
    !newMemberXSellsFlag ||
    !newMemberXSellsFlagEnabled ||
    newMemberXSellsFlag !== 'variant_a' ||
    !isEditCampaignOpen;

  // Hide the customize node for FC on GTB
  const hideCustomizeNode = skipCustomize;
  const expTime = spacetime(`${endTime}Z`);

  const showCart =
    newMemberOnboardingFlagReady &&
    newMemberOnboardingFlag &&
    (step === 'customize' || step === 'boost') &&
    isEditCampaignOpen;

  useEffect(() => {
    Cookies.deleteCookie(NEW_MEMBER_XSELL_FLAG_COOKIE);
    if (newMemberOnboardingFlag && newMemberOnboardingFlagReady) {
      Cookies.setCookie({
        key: NEW_MEMBER_XSELL_FLAG_COOKIE,
        value: newMemberXSellsFlag,
        expireDate: Date.now() + 24 * 60 * 60 * 1000, // 1 day
        maxAge: 1,
      });
    }
  }, [newMemberXSellsFlag, newMemberOnboardingFlag, newMemberOnboardingFlagReady]);

  return (
    <>
      <div
        className={classNames('header', {
          header__nmf: true,
        })}
        data-testid="NMFHeader"
      >
        <div className="header__wrapper">
          <NMFHeaderContent
            showCart={showCart}
            env={globalEnv}
            newMemberState={newMemberState}
            onNewMemberStateFetched={onNewMemberStateFetched}
          />
        </div>
      </div>
      {!!endTime && showTimer && (
        <NmfTimeUp expDate={new Date(expTime.format('iso-utc'))} userRequest={userRequest} />
      )}
      {!!endTime && showTimer && <TimerBar endTime={new Date(expTime.format('iso-utc'))} />}
      {newMemberOnboardingFlagReady && (
        <NewMemberNav
          step={step}
          hideCustomizeNode={hideCustomizeNode}
          hideBoostNode={hideBoostNode}
        />
      )}
      <NavToast loggedIn />
    </>
  );
};

export default NMFHeader;
